import { lightTemplateFragment } from './fragments/light-template.fragment';
import { assetFragment } from './fragments/asset.fragment';
import { GRSRecommendationResponse } from '../../clients/agg-client/agg-client-types';
import { AGGClient } from '../../clients/agg-client/agg-client';
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Template } from '@/content/templates/template.types';
import { CategoryOrder } from '@/templates/software/confluence/templates/confluence-templates-page/src/config/category-template-order';

import { LoaderMatchers } from '../../loader-matcher';
import { SupportedLocales } from '../localization';

import { WAC_TO_CPAPI_LOCALE_MAP } from './constants';

export interface ITemplatePageLoader {
	getQueryData: (
		urlSlug: string,
		locale: string,
		after?: string,
	) => { query: string; variables: TemplateSearchVariables };
	parseData?: (data: any, locale?: string) => any;
	multiplePagesQuery?: boolean;
}
interface ITemplatePageLoaders {
	[key: string]: ITemplatePageLoader;
}

type TemplateSearchQuery = {
	first: number;
	withLocales: string[];
	where: any;
};

type CustomRecoContext = {
	paidSearchTerm: string | null;
};

type RecommendedTemplates = {
	anonymousId: string | undefined;
	userId?: string;
	aggClient?: AGGClient;
	recommendedTemplateIds?: string[];
	custom: CustomRecoContext;
	first?: number;
};
type Templates = {
	aggClient?: AGGClient;
	templateIds?: string[];
};

type TemplateSearchVariables = {
	search?: {
		queries?: Partial<TemplateSearchQuery>;
	};
};

function imageFromContentPlatformImage(
	node: any, //ContentPlatformTemplateImageAsset
) {
	const details = node.details as {
		size: number;
		image?: { height: number; width: number };
	};
	const parsedImage = {
		name: node.title,
		size: details.size,
		url: node.url,
		description: node.description,
		contentType: node.contentType,
	};

	if (details.image) {
		return {
			...parsedImage,
			height: details.image.height,
			width: details.image.width,
		};
	}

	return parsedImage;
}

// for the template-landing loader
function contributorFromContentPlatformContributor(
	node: any, //ContentPlatformTemplate["contributor"]
): any /*Template["contributor"] */ {
	if (node?.__typename === 'ContentPlatformOrganization') {
		return {
			name: node.name,
			tagline: node.tagline,
			shortDescription: node.shortDescription,
			logo: node.logo?.map(imageFromContentPlatformImage),
			industry: node.industry?.map((industry: any) => ({
				name: industry.name,
				shortDescription: industry.shortDescription,
			})),
		};
	} else if (node?.__typename === 'ContentPlatformAuthor') {
		return {
			name: node.name,
			jobTitle: node.jobTitle,
			shortBiography: node.shortBiography,
			authorPicture: node.authorPicture ? imageFromContentPlatformImage(node.authorPicture) : null,
			organization: contributorFromContentPlatformContributor(node.organization),
		};
	}
	return null;
}

export const parseTemplate = (node: any) => {
	return {
		...node,
		contributor: contributorFromContentPlatformContributor(node.contributor),
		templateIcon: node.templateIcon ? imageFromContentPlatformImage(node.templateIcon) : null,
		templatePreview: node.templatePreview?.map(imageFromContentPlatformImage),
		howToUseThisTemplate: node.howToUseThisTemplate?.map((guide: any) => ({
			name: guide.name,
			steps:
				guide.steps?.map((step: any) => ({
					name: step.name,
					description: step.description,
					asset: step.asset ? imageFromContentPlatformImage(step.asset) : null,
				})) ?? [],
		})),
		relatedTemplates: node.relatedTemplates?.map((template: any) => ({
			...template,
			templateIcon: template.templateIcon
				? imageFromContentPlatformImage(template.templateIcon)
				: null,
		})),
	};
};

export const parseCollection = (node: any) => {
	return {
		...node,
		aboutThisCollectionImage: node.aboutThisCollectionImage
			? imageFromContentPlatformImage(node.aboutThisCollectionImage)
			: null,
		collectionIcon: node.collectionIcon ? imageFromContentPlatformImage(node.collectionIcon) : null,
	};
};

export const parseCategories = (edges: any, locale = 'en-US') => {
	const categories = [];
	let localizedName, localizedShortDescriptionOneLiner, experiment;

	for (const { node } of edges) {
		// TODO: GWP-1522 - Remove this check once we are returning delocalized data from CPAPI
		if (typeof node.context.name === 'object' && node.context.name != null) {
			localizedName = node.context.name[locale] ?? node.context.name[`${locale}-fallback`];
		} else {
			localizedName = node.context.name;
		}

		if (
			typeof node.context.shortDescriptionOneLiner === 'object' &&
			node.context.shortDescriptionOneLiner != null
		) {
			localizedShortDescriptionOneLiner =
				node.context.shortDescriptionOneLiner[locale] ??
				node.context.shortDescriptionOneLiner[`${locale}-fallback`];
		} else {
			localizedShortDescriptionOneLiner = node.context.shortDescriptionOneLiner;
		}

		if (node.context.experiment != null) {
			experiment = node.context.experiment;
		} else {
			experiment = false;
		}

		categories.push({
			id: node.context.id,
			name: localizedName,
			shortDescriptionOneLiner: localizedShortDescriptionOneLiner,
			urlSlug: node.context.urlSlug,
			experiment: experiment,
		});
	}
	return categories.sort(
		(a, b) =>
			CategoryOrder[a.urlSlug as keyof typeof CategoryOrder] -
			CategoryOrder[b.urlSlug as keyof typeof CategoryOrder],
	);
};

const GET_RECOMMENDED_TEMPLATE_IDS = `#graphql
query GetTemplateIdRecommendations($anonymousId: ID!, $userId: ID!, $custom: JSON, $first:Int) {
    growthRecommendations {
      recommendations(context: { useCase: "wacConfluenceTemplates", anonymousId: $anonymousId, userId: $userId, product: "marketing", custom: $custom }, first:$first) {
           ... on GrowthRecRecommendations {
              data {
              entityId
            }
        }
      }
    }
  }
`;

const GET_TEMPLATES_BY_ID = `#graphql
    query getTemplates($ids: [String!]) {
        templates(search: {queries: [{ first:75, where: { fieldNamed: "templateId", havingValues: $ids} }]}) {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                node {
                    ...lightTemplateFragment
                }
            }
        }
    }
    ${lightTemplateFragment}
    ${assetFragment}
    `;

export const flattenTemplateImageAssets = (node: any) => {
	if (
		node.__typename === 'ContentPlatformTemplateImageAsset' ||
		node.__typename === 'TemplateImageAsset'
	) {
		return imageFromContentPlatformImage(node);
	}

	for (const key of Object.keys(node)) {
		if (node[key] && typeof node[key] === 'object') {
			node[key] = flattenTemplateImageAssets(node[key]);
		}

		if (Array.isArray(node[key])) {
			node[key] = node[key].map(flattenTemplateImageAssets);
		}
	}

	return node;
};

export const TemplatePageLoaders: ITemplatePageLoaders = {};

export const getTemplateData = async (
	route: string,
	locale: SupportedLocales = 'en',
	aggClient: AGGClient = new AGGClient(),
) => {
	const matcher = Object.values(LoaderMatchers).find(
		(loader) => loader.match.test(route) && !loader.requireMlTemplateIds,
	);

	const cpapiLocale = WAC_TO_CPAPI_LOCALE_MAP[locale] || 'en-US';

	if (!matcher) {
		console.warn(`No loader matcher found for route ${route}`);
		return;
	}

	const templatePageLoader = TemplatePageLoaders[matcher.name];

	if (!templatePageLoader) {
		console.warn(`No template page loader found for route ${route}`);
		return;
	}

	const { getQueryData, parseData, multiplePagesQuery } = templatePageLoader;

	const matchGroup = matcher.match.exec(route);
	let urlSlug = '';

	if (matchGroup && matchGroup.groups) {
		urlSlug = matchGroup.groups['urlSlug'] as string;
	}

	const { query, variables } = getQueryData(urlSlug, cpapiLocale);

	const { data } = await aggClient.query(query, variables);

	// this is only for grabbing additional Confluence templates
	if (matcher.name !== 'agile-overview-page') {
		while (
			multiplePagesQuery &&
			data.templates.pageInfo?.hasNextPage &&
			data.templates.pageInfo?.endCursor
		) {
			const { query: followUpQuery, variables: followUpVariables } = getQueryData(
				urlSlug,
				cpapiLocale,
				data.templates.pageInfo.endCursor,
			);
			const { data: newData } = await aggClient.query(followUpQuery, {
				...followUpVariables,
			});
			data.templates.edges.push(...newData.templates.edges);
			data.templates.pageInfo = newData.templates.pageInfo;
		}
	}

	return parseData ? parseData(data, cpapiLocale) : data;
};

export const getRecommendedTemplates = async ({
	anonymousId,
	userId,
	aggClient = new AGGClient(),
	recommendedTemplateIds,
	custom,
	first = 9,
}: RecommendedTemplates) => {
	let recommendedIds: string[] = [];

	const { data: recommendationData, errors } = await aggClient.query<GRSRecommendationResponse>(
		GET_RECOMMENDED_TEMPLATE_IDS,
		{
			anonymousId: anonymousId,
			userId: userId,
			custom: custom,
			first: first,
		},
	);
	// will use fallback if one available
	if (errors) {
		console.error(`GRS API failed`, errors[0]?.message);
		if (recommendedTemplateIds) {
			// Load data from browser storage
			recommendedIds = recommendedTemplateIds;
		}
	} else {
		recommendedIds = recommendationData.growthRecommendations.recommendations.data.map(
			(recommendation) => recommendation.entityId,
		);
	}

	const { data } = await aggClient.query(GET_TEMPLATES_BY_ID, {
		ids: recommendedIds,
	});
	return {
		recommendedTemplateIds: recommendedIds,
		templates: data.templates.edges.map((edge) => edge.node) as unknown as Template[],
	};
};

export const getTemplatesById = async ({ aggClient = new AGGClient(), templateIds }: Templates) => {
	const { data } = await aggClient.query(GET_TEMPLATES_BY_ID, {
		ids: templateIds,
	});

	return data.templates.edges.map((edge) => edge.node) as unknown as Template[];
};
